import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import searchService from '../../api/searchService';
import { useFilters } from '../../context/FilterContext';
import { useNotifications } from '../../context/NotificationContext';
import useDebounce from '../../hooks/useDebounce';
import TechnicianCard from './TechnicianCard';
import AdvancedFilters from '../client/AdvancedFilters';
import Pagination from '../common/Pagination';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';

export default function TechnicianList() {
  const { t } = useTranslation();
  const { filters, asQuery, update, setPage } = useFilters();
  const notify = useNotifications();
  const [params] = useSearchParams();
  const [page, setPageData] = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [loading, setLoading] = useState(true);
  const seeded = useRef(false);

  // Seed filters from URL (?technicianTypeId / ?keyword / ?city) once.
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    const patch = {};
    if (params.get('technicianTypeId')) patch.technicianTypeId = params.get('technicianTypeId');
    if (params.get('keyword')) patch.keyword = params.get('keyword');
    if (params.get('city')) patch.city = params.get('city');
    if (Object.keys(patch).length) update(patch);
  }, [params, update]);

  const key = useMemo(() => JSON.stringify(asQuery), [asQuery]);
  const debouncedKey = useDebounce(key, 350);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    searchService
      .listTechnicians(JSON.parse(debouncedKey))
      .then((res) => !cancelled && setPageData(res))
      .catch((err) => !cancelled && notify.apiError(err))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [debouncedKey, notify]);

  return (
    <div className="page">
      <div className="container">
        <div className="page-head">
          <div>
            <h1>{t('technician.browseTitle')}</h1>
            <p>{page.totalElements} {t('technician.available')}</p>
          </div>
        </div>

        <div className="split">
          <AdvancedFilters />
          <div>
            {loading ? (
              <LoadingSpinner />
            ) : page.content.length === 0 ? (
              <EmptyState icon="search" title={t('technician.noMatch')}>
                {t('technician.widenSearch')}
              </EmptyState>
            ) : (
              <>
                <div className="grid cards">
                  {page.content.map((t) => <TechnicianCard key={t.id} tech={t} />)}
                </div>
                <Pagination
                  page={filters.page}
                  totalPages={page.totalPages}
                  onChange={setPage}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
